import React, { useEffect, useState } from 'react'
import { useTheme } from '../../../../components/hooks/useTheme';
import useAuth from '../../../../components/hooks/useAuth';
import { Button, Dialog } from '@mui/material';
import axios from 'axios';
import baseURL from '../../../../configs/api';
import PleaseWaitPopup from '../../../PleaseWaitPopup';
import colorConfigs from '../../../../configs/colorConfigs';
import CustomFormatDatePicker from '../../../../components/common/CustomFormatDatePicker';
import { validateMin } from '../../../../utils/validateMin';
import useDelete from '../../../../components/hooks/useDelete';
import useGeneral from '../../../../components/hooks/useGeneral';
import Cookies from 'js-cookie';
import useTenant from '../../../../components/hooks/useTenant';

type AddRelocationProps = {
    idNumber: any;
    openEditRelocation: boolean;
    setOpenEditRelocation: (value: boolean) => void;
    setRefreshRelocationList: (value: boolean) => void;
    relocationId: number;
}

export const EditRelocation = ({ idNumber, openEditRelocation, setOpenEditRelocation, setRefreshRelocationList, relocationId }: AddRelocationProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const { dateFormat }: any = useGeneral();
    const [relocationVehicleDate, setRelocationVehicleDate] = useState<string>("");
    const [relocationVehicleTime, setRelocationVehicleTime] = useState<string>("");
    const [toLocationId, setToLocationId] = useState<string>("");
    const [fromLocationId, setFromLocationId] = useState<string>("");
    const [toLocations, setToLocations] = useState<any[]>([]);
    const [oldOdometer, setOldOdometer] = useState<number>(0);
    const [newOdometer, setNewOdometer] = useState<number>(0);
    const [oldFuelLevel, setOldFuelLevel] = useState<string>("");
    const [newFuelLevel, setNewFuelLevel] = useState<string>("");
    const [status, setStatus] = useState<string>("1");
    const [reason, setReason] = useState<string>("");
    const [createdAt, setCreatedAt] = useState<string>("");
    const [createdBy, setCreatedBy] = useState<string>("");
    const [pleaseWait, setPleaseWait] = useState<boolean>(false);

    const fetchLocations = (excludeId: string) => {
        axios.get(`${baseURL}location`, {
            headers: {
                'X-Tenant-ID': tenantId,
            }
        })
            .then((response) => {
                const filtered = response?.data?.filter((item: any) => item.id !== excludeId);
                setToLocations(
                    filtered?.map((item: any) => ({
                        value: item.id,
                        label: item.locationName
                    }))
                );
                handleTenantError(null);
            })
            .catch((error) => {
                setToLocations([]);
                handleTenantError(error);
            });
    };

    useEffect(() => {
        if (relocationId > 0) {
            setPleaseWait(true);

            axios.get(`${baseURL}relocation/${relocationId}`, {
                headers: {
                    'X-Tenant-ID': tenantId,
                }
            })
                .then((response) => {
                    setRelocationVehicleDate(response?.data?.relocationDate?.split("T")[0]);
                    setRelocationVehicleTime(response?.data?.relocationDate?.split("T")[1]);
                    setToLocationId(response?.data?.toLocationId);
                    setFromLocationId(response?.data?.fromLocationId);
                    setOldOdometer(response?.data?.oldOdometer);
                    setNewOdometer(response?.data?.newOdometer);
                    setOldFuelLevel(response?.data?.oldFuelLevel);
                    setNewFuelLevel(response?.data?.newFuelLevel);
                    setReason(response?.data?.reason);
                    setCreatedAt(response?.data?.createdAt);
                    setCreatedBy(response?.data?.createdBy);
                    setStatus(response?.data?.status);
                    fetchLocations(response?.data?.fromLocationId);
                    setPleaseWait(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setPleaseWait(false);
                    handleTenantError(error);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relocationId]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!pleaseWait) {
            setPleaseWait(true);
            axios
                .put(
                    `${baseURL}relocation?userId=${auth?.id}`,
                    JSON.stringify({
                        id: relocationId,
                        vehicleId: idNumber?.state,
                        fromLocationId,
                        toLocationId,
                        reason,
                        oldOdometer,
                        newOdometer,
                        oldFuelLevel,
                        newFuelLevel,
                        relocationDate: new Date(relocationVehicleDate + 'T' + relocationVehicleTime)?.toLocaleString(),
                        status,
                        createdAt,
                        createdBy,
                        updatedAt: new Date().toLocaleString(),
                        updatedBy: auth?.givenname,
                    }),
                    {
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                        withCredentials: true,
                    }
                )
                .then(() => {
                    setPleaseWait(false);
                    setOpenEditRelocation(false);
                    setRefreshRelocationList(true);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setPleaseWait(false);
                    handleTenantError(error);
                });
        }
    };

    const deleteRelocation = () => {
        setDeleteData({
            tilte: 'Delete',
            summary: "Are you sure you want to delete this relocation?",
            confirmBtn: 'Yes',
            isDelete: (callback: any) => {
                setPleaseWait(true);

                fetch(`${baseURL}relocation?id=${relocationId}&userId=${auth?.id}`, {
                    method: 'DELETE',
                    headers: {
                        'X-Tenant-ID': tenantId,
                    }
                })
                    .then(() => {
                        setPleaseWait(false);
                        setOpenEditRelocation(false);
                        setRefreshRelocationList(true);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        setPleaseWait(false);
                        handleTenantError(error);
                    });

                callback();
            }
        });
        setDeletePopup(true);
    };

    return (
        <>
            <Dialog
                open={openEditRelocation}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '550px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[10px] py-[5px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>

                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Edit Relocate Vehicle</h2>

                                    <span className={`material-symbols-outlined text-[25px] font-light cursor-pointer ${themeStyles.main.textHeadingColor}`} onClick={() => setOpenEditRelocation(false)}>close</span>

                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="grid grid-cols-2 gap-6 mt-4">
                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Relocation Date</label>
                                                <CustomFormatDatePicker
                                                    value={relocationVehicleDate}
                                                    onChange={setRelocationVehicleDate}
                                                    dateFormat={dateFormat}
                                                    required={true}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Relocation Time</label>
                                                <input
                                                    type="time"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={relocationVehicleTime}
                                                    onChange={(e) => {
                                                        setRelocationVehicleTime(e.target.value)
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>To</label>
                                                <select
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={toLocationId}
                                                    onChange={(e) => setToLocationId(e.target.value)}
                                                    required
                                                >
                                                    {toLocations.map((item: any) => {
                                                        return <option className={`${themeStyles.main.tailwindSectionBg}`} key={item.value} value={item.value}>{item.label}</option>
                                                    })}
                                                </select>

                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Status</label>
                                                <select
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={status}
                                                    onChange={(e) => setStatus(e.target.value)}
                                                    required
                                                >
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={"1"}>Not Started</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={"2"}>In Transit</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={"3"}>Completed</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Odometer</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={newOdometer}
                                                    onChange={(e) => setNewOdometer(validateMin(e.target.value, 0))}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Fuel Level</label>
                                                <select
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={newFuelLevel}
                                                    onChange={(e) => setNewFuelLevel(e.target.value)}
                                                    required
                                                >
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={0}>0</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={1}>1</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={2}>2</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={3}>3</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={4}>4</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={5}>5</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={6}>6</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={7}>7</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={8}>8</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="w-full mt-4">
                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Reason</label>
                                        <textarea
                                            className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            rows={6}
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                    </div>

                                    <div className="mt-4 w-full flex gap-4">
                                        <Button variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.main.bg
                                            }}
                                            type="submit"
                                        >
                                            Save
                                        </Button>

                                        <Button variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.cancel.bg,
                                                border: colorConfigs.btn.cancel.border,
                                                color: colorConfigs.btn.cancel.color
                                            }}
                                            onClick={() => setOpenEditRelocation(false)}
                                        >
                                            Cancel
                                        </Button>

                                        <Button variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.delete.bg
                                            }}
                                            onClick={() => deleteRelocation()}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWait} />
        </>
    )
}